import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/lib/auth'
import App from './App.tsx'
import './index.css'
import { registerSyncfusionLicense } from '@/lib/syncfusion-license'
import { clearUserItemsInLocalStorage } from '@/lib/auth/logout'
import localizationService, { yardiTranslationConfig, initTranslations, i18n } from 'yardi-react-translation/translationService';
import { initializeDynamicPrimaryRgb } from '@/lib/themeUtils'
import { getPropertyId } from '@/hooks/useApiSequencing'
import { LoadingScreenVisual } from '@/components/ui/ApplicationLoadingScreen'

yardiTranslationConfig.languageApiBase = '/onlineleasingstudent';
yardiTranslationConfig.parameters = {
  propertyId: getPropertyId() || '0',
  productName: 'onlineleasingstudent'
};

// Pre-React guard: ensure clean state for securelogin links opened from other sites/new tabs
(function ensureCleanStateForSecureLogin() {
  try {
    const path = window.location.pathname.toLowerCase()
    const isAccessSecureLogin = path.includes('/onlineleasingstudent/content3/access/') && path.endsWith('/securelogin')
    if (!isAccessSecureLogin) return

    const url = new URL(window.location.href)
    const hasReset = url.searchParams.get('slreset') === '1'
    if (hasReset) return

    // Clear all app storage and cookies, then reload once with a guard flag
    clearUserItemsInLocalStorage()
    url.searchParams.set('slreset', '1')
    window.location.replace(url.toString())
  } catch {
    // no-op
  }
})()

// Lazy load heavy dependencies
const initializeHeavyDependencies = async () => {
  // Lazy load FontAwesome
  await import('@/lib/fontawesome')
  // Lazy load global API configuration
  await import('@/lib/api')
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - increased from 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes garbage collection
      retry: 1, // Reduced retries to prevent excessive requests
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      refetchOnReconnect: false, // Prevent refetch on reconnect
      refetchOnMount: false, // Prevent refetch on component mount if data exists
    },
  },
})

// Pre-provider loading screen using the pure visual component (no hooks)
const TranslationLoader = () => <LoadingScreenVisual />


const renderApp = async () => {
  const root = ReactDOM.createRoot(document.getElementById('root')!)
  try {
    
    root.render(
      <React.StrictMode>
        <TranslationLoader />
      </React.StrictMode>
    )
    // Initialize translations
    console.log('🌐 Initializing translations...');
    initTranslations();

    // Wait for translations with TIMEOUT
    const translationTimeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Translation loading timeout')), 5000) // 5 second timeout
    );
    
    try {
      await Promise.race([
        localizationService.waitForReady(),
        translationTimeout
      ]);
      console.log('✅ Translations ready, rendering app');
    } catch (timeoutError) {
      console.warn('⚠️ Translation loading failed or timed out:', timeoutError);
      console.warn('⚠️ App will render without waiting for translations');
      // Continue anyway - app will render with fallback to English or show keys
    }

    // Initialize heavy dependencies in the background
    initializeHeavyDependencies().catch(console.error);
    // syncfusion license
    registerSyncfusionLicense();
    // Initialize dynamic primary RGB support
    initializeDynamicPrimaryRgb();
  } catch {
    console.log('Failed to init syncfusion license');
  }

  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

renderApp();